import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PropertyThumbnail, Section, StatTile } from "./drawer-primitives";
import { formatCityLine, PropertyMeta } from "@/lib/property-meta";
import { cn } from "@/lib/utils";
import { AlertTriangle, CalendarDays, CheckCircle2, ClipboardList, Clock, DollarSign, Home, Loader2, NotebookPen, Play, User, X } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { WorkOrder } from "@/hooks/use-property-management";
import { format } from "date-fns";

interface WorkOrderDetailDrawerProps {
  workOrder: WorkOrder | null;
  open: boolean;
  property?: PropertyMeta;
  onOpenChange: (open: boolean) => void;
  onUpdate: (wo: Partial<WorkOrder> & { id: string }) => void;
  isSaving: boolean;
}

const CATEGORIES = ["general", "plumbing", "electrical", "hvac", "appliance", "roofing", "landscaping", "pest_control", "other"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

const STATUS_CONFIG: Record<string, { label: string; headline: string; chip: string; accent: string; bar: string; step: number; icon: React.ReactNode }> = {
  open: {
    label: "Open", headline: "Awaiting work", step: 0,
    chip: "bg-destructive/15 text-destructive", accent: "border-destructive/30 bg-destructive/10", bar: "bg-destructive",
    icon: <AlertTriangle className="h-3 w-3" />,
  },
  in_progress: {
    label: "In Progress", headline: "Work in progress", step: 1,
    chip: "bg-warning/15 text-warning", accent: "border-warning/30 bg-warning/10", bar: "bg-warning",
    icon: <Clock className="h-3 w-3" />,
  },
  completed: {
    label: "Completed", headline: "Work completed", step: 2,
    chip: "bg-success/15 text-success", accent: "border-success/30 bg-success/10", bar: "bg-success",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
};

const money = (n: number | null | undefined) => (n != null ? `$${n.toLocaleString()}` : "—");

const todayString = () => format(new Date(), "yyyy-MM-dd");

/** `completed_at` is a timestamptz — read the local calendar day out of it. */
const completedAtToDateInput = (completedAt: string | null) =>
  completedAt ? format(new Date(completedAt), "yyyy-MM-dd") : "";

/**
 * "YYYY-MM-DD" → a local-noon timestamp. Midnight would land on the previous
 * day once Postgres stores it as UTC for anyone west of Greenwich, so the day
 * the user picked is the day that reads back.
 */
const dateInputToCompletedAt = (date: string) =>
  new Date(`${date || todayString()}T12:00:00`).toISOString();

type Draft = Pick<WorkOrder, "title" | "description" | "category" | "priority" | "vendor_name" | "vendor_phone" | "due_date" | "cost_estimate" | "cost_actual" | "notes">;

function toDraft(wo: WorkOrder): Draft {
  return {
    title: wo.title,
    description: wo.description,
    category: wo.category,
    priority: wo.priority,
    vendor_name: wo.vendor_name,
    vendor_phone: wo.vendor_phone,
    due_date: wo.due_date,
    cost_estimate: wo.cost_estimate,
    cost_actual: wo.cost_actual,
    notes: wo.notes,
  };
}

export function WorkOrderDetailDrawer({ workOrder, open, property, onOpenChange, onUpdate, isSaving }: WorkOrderDetailDrawerProps) {
  const { user } = useAuth();
  const [draft, setDraft] = useState<Draft | null>(null);
  /**
   * Kept out of `draft` on purpose: "Save Changes" writes the whole draft, and
   * an open work order must never come away with a completion date.
   */
  const [completionDate, setCompletionDate] = useState("");

  useEffect(() => {
    setDraft(workOrder ? toDraft(workOrder) : null);
    setCompletionDate(completedAtToDateInput(workOrder?.completed_at ?? null));
    // Reset the draft only when a different work order is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrder?.id]);

  if (!workOrder || !draft) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px] [&>button:last-of-type]:hidden">
          <SheetTitle className="sr-only">Work order</SheetTitle>
        </SheetContent>
      </Sheet>
    );
  }

  const status = workOrder.status;
  const editable = status !== "completed";
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.open;
  const addr2 = formatCityLine(property);
  const costActualValid = draft.cost_actual != null && Number.isFinite(draft.cost_actual) && draft.cost_actual >= 0;
  const variance = draft.cost_estimate != null && draft.cost_actual != null ? draft.cost_actual - draft.cost_estimate : null;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));
  const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));

  const persist = (extra: Partial<WorkOrder> = {}) => {
    onUpdate({ id: workOrder.id, ...draft, ...extra });
  };

  const handleStart = () => {
    // Stamp the start date only the first time work begins.
    persist({ status: "in_progress", started_at: workOrder.started_at ?? todayString() });
  };

  const handleComplete = () => {
    if (!costActualValid) return;
    persist({
      status: "completed",
      completed_at: dateInputToCompletedAt(completionDate),
      completed_by: user?.id ?? null,
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px] [&>button:last-of-type]:hidden"
      >
        <SheetTitle className="sr-only">{workOrder.title}</SheetTitle>
        <SheetDescription className="sr-only">Work order details for {property?.address ?? "this property"}</SheetDescription>

        {/* Header */}
        <div className="flex items-start gap-3.5 border-b border-border px-6 py-5">
          <PropertyThumbnail
            imageUrl={property?.imageUrl}
            alt={property?.address ?? "Property"}
            fallback={<Home className="h-7 w-7" />}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="truncate text-lg font-extrabold text-foreground">{property?.address ?? "Property"}</h2>
            {addr2 && <div className="truncate text-sm text-muted-foreground">{addr2}</div>}
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold", cfg.chip)}>
                {cfg.icon} {cfg.label}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-md text-muted-foreground transition hover:bg-muted/40 hover:text-foreground"
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* Body — disabled wholesale once completed; reopening re-enables editing. */}
        <fieldset disabled={!editable} className="min-w-0 flex-1 space-y-3.5 overflow-y-auto px-6 py-5 disabled:opacity-100">
          {/* Status overview */}
          <section className={cn("rounded-lg border p-4", cfg.accent)}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-bold text-foreground">{cfg.headline}</span>
              <Badge
                variant={draft.priority === "urgent" ? "destructive" : draft.priority === "high" ? "default" : "outline"}
                className="capitalize"
              >
                {draft.priority} priority
              </Badge>
            </div>
            <div className="flex gap-1" aria-hidden>
              {[0, 1, 2].map((i) => (
                <div key={i} className={cn("h-1.5 flex-1 rounded-full transition-colors", i <= cfg.step ? cfg.bar : "bg-border")} />
              ))}
            </div>
          </section>

          {/* Details */}
          <Section icon={<ClipboardList className="h-3.5 w-3.5" />} title="Details">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wo-title">Title</Label>
                <Input id="wo-title" value={draft.title ?? ""} onChange={(e) => set("title", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wo-description">Description</Label>
                <Textarea id="wo-description" rows={3} value={draft.description ?? ""} onChange={(e) => set("description", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={draft.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger className="bg-card"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={draft.priority} onValueChange={(v) => set("priority", v)}>
                    <SelectTrigger className="bg-card"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </Section>

          {/* Vendor & schedule */}
          <Section icon={<User className="h-3.5 w-3.5" />} title="Vendor & schedule">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wo-vendor">Vendor Name</Label>
                <Input id="wo-vendor" className="bg-card" value={draft.vendor_name ?? ""} onChange={(e) => set("vendor_name", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wo-vendor-phone">Vendor Phone</Label>
                <Input id="wo-vendor-phone" className="bg-card" value={draft.vendor_phone ?? ""} onChange={(e) => set("vendor_phone", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wo-due">Due Date</Label>
                <DateInput id="wo-due" className="bg-card" value={draft.due_date ?? ""} onChange={(e) => set("due_date", e.target.value || null)} />
              </div>
              {workOrder.started_at && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-warning" /> Started</Label>
                  <div className="flex h-10 items-center rounded-md border border-border bg-card px-3 text-sm text-muted-foreground">
                    {format(new Date(`${workOrder.started_at}T12:00:00`), "MMM d, yyyy")}
                  </div>
                </div>
              )}
              {workOrder.completed_at ? (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-success" /> Completed</Label>
                  <div className="flex h-10 items-center rounded-md border border-border bg-card px-3 text-sm text-muted-foreground">
                    {format(new Date(workOrder.completed_at), "MMM d, yyyy")}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="wo-completed" className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5 text-success" /> Completion Date
                  </Label>
                  <DateInput
                    id="wo-completed"
                    className="bg-card"
                    value={completionDate}
                    min={workOrder.started_at ?? undefined}
                    max={todayString()}
                    onChange={(e) => setCompletionDate(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Defaults to today when left blank.</p>
                </div>
              )}
            </div>
          </Section>

          {/* Cost */}
          <Section icon={<DollarSign className="h-3.5 w-3.5" />} title="Cost">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wo-estimate">Cost Estimate ($)</Label>
                <Input id="wo-estimate" type="number" min="0" className="bg-card" value={draft.cost_estimate ?? ""} onChange={(e) => set("cost_estimate", numOrNull(e.target.value))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wo-actual">Actual Cost ($)</Label>
                <Input id="wo-actual" type="number" min="0" className="bg-card" value={draft.cost_actual ?? ""} onChange={(e) => set("cost_actual", numOrNull(e.target.value))} placeholder="Final cost" />
              </div>
            </div>
            {variance != null && (
              <div className="mt-3">
                <StatTile
                  label={variance > 0 ? "Over budget" : variance < 0 ? "Under budget" : "On budget"}
                  value={money(Math.abs(variance))}
                  valueClassName={variance > 0 ? "text-destructive" : "text-success"}
                />
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Actual cost is required to mark completed. Feeds completed work orders into accounting as a maintenance expense.
            </p>
          </Section>

          {/* Notes */}
          <Section icon={<NotebookPen className="h-3.5 w-3.5" />} title="Notes">
            <Textarea
              value={draft.notes ?? ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Vendor updates, scheduling notes, follow-ups…"
              className="min-h-[72px] resize-y bg-card"
            />
          </Section>
        </fieldset>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-2.5 border-t border-border bg-card px-6 py-3.5">
          {editable ? (
            <>
              {status === "open" && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" onClick={handleStart} disabled={isSaving}>
                        <Play className="h-4 w-4 mr-2" /> Start Work
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Marks this work order as In Progress and records today as the start date.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button variant="outline" className="ml-auto" onClick={() => persist()} disabled={isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Changes
              </Button>
              <Button className="bg-success text-success-foreground hover:bg-success/90" onClick={handleComplete} disabled={isSaving || !costActualValid}>
                <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Completed
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">Completed — reopen to edit.</p>
              <Button variant="outline" className="ml-auto" onClick={() => persist({ status: "open", completed_at: null })} disabled={isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Reopen
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
